import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  BudgetReport,
  BudgetCategory,
  BudgetTransaction,
  BudgetHistoricalReportMeta,
  BudgetStatus,
} from '@/types/entities/community_sub-elects/budgetTransparency';
import {
  glastonburyBudgetReport,
  glastonburyBudgetTransactions,
  glastonburyHistoricalReports,
} from '@/data/entities/community_sub-elects/glastonburyBudget';

interface CreateBudgetCategoryInput {
  name: string;
  description: string;
  budgetedAmount: number;
  spentAmount: number;
  color: string;
  status: BudgetStatus;
  lastUpdated?: string;
}

interface UpdateBudgetCategoryInput extends Partial<CreateBudgetCategoryInput> {}

interface BudgetTransparencyState {
  report: BudgetReport;
  transactions: BudgetTransaction[];
  historicalReports: BudgetHistoricalReportMeta[];
  addCategory: (input: CreateBudgetCategoryInput) => BudgetCategory;
  updateCategory: (id: string, updates: UpdateBudgetCategoryInput) => void;
  removeCategory: (id: string) => void;
}

const generateId = () => {
  if (typeof globalThis !== 'undefined' && globalThis.crypto && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }

  return `category-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const calculateCategoryMetrics = (budgetedAmount: number, spentAmount: number) => {
  const safeBudget = Number.isFinite(budgetedAmount) ? budgetedAmount : 0;
  const safeSpent = Number.isFinite(spentAmount) ? spentAmount : 0;

  const remainingAmount = Number((safeBudget - safeSpent).toFixed(2));
  const percentage = safeBudget === 0 ? 0 : Number(((safeSpent / safeBudget) * 100).toFixed(1));

  return { remainingAmount, percentage };
};

const recalculateReportTotals = (report: BudgetReport) => {
  const totalBudget = report.categories.reduce((sum, category) => sum + category.budgetedAmount, 0);
  const totalSpent = report.categories.reduce((sum, category) => sum + category.spentAmount, 0);

  report.totalBudget = Number(totalBudget.toFixed(2));
  report.totalSpent = Number(totalSpent.toFixed(2));
};

const cloneReport = (report: BudgetReport): BudgetReport => ({
  ...report,
  categories: report.categories.map((category) => ({ ...category })),
});

export const useBudgetTransparencyStore = create(
  immer<BudgetTransparencyState>((set) => {
    const initialReport = cloneReport(glastonburyBudgetReport);
    recalculateReportTotals(initialReport);

    return {
      report: initialReport,
      transactions: glastonburyBudgetTransactions.map((transaction) => ({ ...transaction })),
      historicalReports: glastonburyHistoricalReports.map((meta) => ({ ...meta })),

      addCategory: (input) => {
        const lastUpdated = input.lastUpdated || new Date().toISOString();
        const { remainingAmount, percentage } = calculateCategoryMetrics(
          input.budgetedAmount,
          input.spentAmount,
        );

        const newCategory: BudgetCategory = {
          id: generateId(),
          name: input.name,
          description: input.description,
          budgetedAmount: input.budgetedAmount,
          spentAmount: input.spentAmount,
          remainingAmount,
          percentage,
          color: input.color,
          status: input.status,
          lastUpdated,
        };

        set((state) => {
          state.report.categories.unshift(newCategory);
          recalculateReportTotals(state.report);
        });

        return newCategory;
      },

      updateCategory: (id, updates) => {
        set((state) => {
          const categoryIndex = state.report.categories.findIndex((category) => category.id === id);
          if (categoryIndex === -1) return;

          const existing = state.report.categories[categoryIndex];
          const budgetedAmount = updates.budgetedAmount ?? existing.budgetedAmount;
          const spentAmount = updates.spentAmount ?? existing.spentAmount;
          const metrics = calculateCategoryMetrics(budgetedAmount, spentAmount);

          state.report.categories[categoryIndex] = {
            ...existing,
            ...updates,
            budgetedAmount,
            spentAmount,
            remainingAmount: metrics.remainingAmount,
            percentage: metrics.percentage,
            lastUpdated: updates.lastUpdated ?? existing.lastUpdated,
          };

          recalculateReportTotals(state.report);
        });
      },

      removeCategory: (id) => {
        set((state) => {
          const nextCategories = state.report.categories.filter((category) => category.id !== id);
          if (nextCategories.length === state.report.categories.length) return;

          state.report.categories = nextCategories;
          recalculateReportTotals(state.report);
        });
      },
    };
  }),
);
